import type { Project } from '../types';
import { parseJSON } from '../components/ProjectForm';
import { effectiveProjectRole } from '../proposals/roles-projet/projectRoles';

// Standard full-time week used to convert a scoping allocation % into
// hours/week. Mirrors ProjectDetailPage's local HOURS_PER_WEEK — kept here
// too so cross-project views (Gérance resources) don't duplicate the number.
export const HOURS_PER_WEEK = 40;

export interface PersonProjectAllocation {
  projectId: number;
  projectName: string;
  role: string;         // free-text précision
  projectRole: string;  // [Proposition: roles-projet] one of the four roles ('' if unset)
  pct: number;
  hoursPerWeek: number;
}

// A person's collaborator allocations, read from each project's
// `scoping.allocations[]` (the real role/% source — resources_effort.collaborators
// only ever has name + a hard-coded 0 allocation, see ProjectForm's CollaboratorsSection).
export function getPersonAllocations(personName: string, projects: Project[]): PersonProjectAllocation[] {
  const n = personName.trim().toLowerCase();
  const rows: PersonProjectAllocation[] = [];
  for (const p of projects) {
    const scoping = parseJSON<any>(p.scoping, {});
    const allocations = Array.isArray(scoping.allocations) ? scoping.allocations : [];
    for (const a of allocations) {
      if ((a.name || '').trim().toLowerCase() !== n) continue;
      const pct = parseFloat(a.allocation_pct) || 0;
      rows.push({ projectId: p.id, projectName: p.name, role: a.role || '', projectRole: effectiveProjectRole(a.name || '', a.project_role, parseJSON<any>(p.ownership, {}).sponsor), pct, hoursPerWeek: HOURS_PER_WEEK * pct / 100 });
    }
  }
  return rows;
}

export interface ProjectCollaborator {
  name: string;
  role: string;         // free-text précision
  projectRole: string;  // [Proposition: roles-projet] one of the four roles ('' if unset)
  pct: number;
  hoursPerWeek: number;
}

// Every collaborator allocated on one project (for "who else is on this
// project" expansion), optionally excluding one name — the person whose row
// this expands from, already shown above.
export function getProjectCollaborators(project: Project, excludeName?: string): ProjectCollaborator[] {
  const exclude = excludeName?.trim().toLowerCase();
  const scoping = parseJSON<any>(project.scoping, {});
  const allocations = Array.isArray(scoping.allocations) ? scoping.allocations : [];
  return allocations
    .filter((a: any) => (a.name || '').trim() && (a.name || '').trim().toLowerCase() !== exclude)
    .map((a: any) => {
      const pct = parseFloat(a.allocation_pct) || 0;
      return { name: a.name, role: a.role || '', projectRole: effectiveProjectRole(a.name || '', a.project_role, parseJSON<any>(project.ownership, {}).sponsor), pct, hoursPerWeek: HOURS_PER_WEEK * pct / 100 };
    });
}

export interface PersonSponsorProject {
  projectId: number;
  projectName: string;
}

export function getPersonSponsorProjects(personName: string, projects: Project[]): PersonSponsorProject[] {
  const n = personName.trim().toLowerCase();
  return projects
    .filter(p => (parseJSON<any>(p.ownership, {}).sponsor || '').trim().toLowerCase() === n)
    .map(p => ({ projectId: p.id, projectName: p.name }));
}

export interface PersonProjectInvolvement {
  projectId: number;
  projectName: string;
  isSponsor: boolean;
  isCollaborator: boolean;
  role: string;         // '' when sponsor-only — no allocation entry to read a role from
  projectRole: string;  // [Proposition: roles-projet] 'sponsor' for sponsor-only rows
  pct: number;           // 0 when sponsor-only
  hoursPerWeek: number;  // 0 when sponsor-only
}

// One row per project the person touches, merging sponsor and collaborator
// relationships instead of listing the same project twice — a project where
// they're both shows a single row with both flags set.
export function getPersonInvolvement(personName: string, projects: Project[]): PersonProjectInvolvement[] {
  const byProject = new Map<number, PersonProjectInvolvement>();
  for (const a of getPersonAllocations(personName, projects)) {
    byProject.set(a.projectId, {
      projectId: a.projectId, projectName: a.projectName,
      isSponsor: false, isCollaborator: true,
      role: a.role, projectRole: a.projectRole, pct: a.pct, hoursPerWeek: a.hoursPerWeek,
    });
  }
  for (const s of getPersonSponsorProjects(personName, projects)) {
    const existing = byProject.get(s.projectId);
    if (existing) existing.isSponsor = true;
    else byProject.set(s.projectId, {
      projectId: s.projectId, projectName: s.projectName,
      isSponsor: true, isCollaborator: false,
      role: '', projectRole: 'sponsor', pct: 0, hoursPerWeek: 0,
    });
  }
  return [...byProject.values()];
}
